import {useMemo} from 'react';
import dayjs from 'dayjs';
import {usePeriodHook} from '@hooks/period-hook';

export interface GoalPeriodOption {
  label: string;
  value: string;
  starts?: string;
  ends?: string;
  disabled?: boolean;
}

const compactRange = (starts: string, ends: string) => {
  const start = dayjs(starts);
  const end = dayjs(ends);
  const years =
    start.year() === end.year()
      ? `${end.year()}`
      : `${start.year()}–${end.year()}`;

  return `${start.format('MMM')}–${end.format('MMM')} ${years}`;
};

export const useGoalPeriods = () => {
  const {periods, currentMonthRange} = usePeriodHook();

  const goalPeriods = useMemo<GoalPeriodOption[]>(() => {
    const quarterPeriods = periods
      .filter(
        (period) => period.value !== 'custom' && period.starts && period.ends,
      )
      .map((period, index) => ({
        label: `Q${index + 1}: ${compactRange(period.starts, period.ends)}`,
        value: `quarter-${index + 1}-${period.starts}`,
        starts: period.starts,
        ends: period.ends,
        disabled: period.disabled,
      }));

    if (!quarterPeriods.length) {
      return [
        {
          label: 'Custom date range',
          value: 'custom',
        },
      ];
    }

    const now = dayjs().startOf('day');
    const availableQuarters = quarterPeriods.filter(
      (period) => !period.ends || dayjs(period.ends).endOf('day').isAfter(now),
    );

    const halfPeriods = [
      {
        label: `H1: ${compactRange(
          quarterPeriods[0].starts || '',
          quarterPeriods[1].ends || '',
        )}`,
        value: `half-1-${quarterPeriods[0].starts}`,
        starts: quarterPeriods[0].starts,
        ends: quarterPeriods[1].ends,
        disabled: quarterPeriods[0].disabled || quarterPeriods[1].disabled,
      },
      {
        label: `H2: ${compactRange(
          quarterPeriods[2].starts || '',
          quarterPeriods[3].ends || '',
        )}`,
        value: `half-2-${quarterPeriods[2].starts}`,
        starts: quarterPeriods[2].starts,
        ends: quarterPeriods[3].ends,
        disabled: quarterPeriods[2].disabled || quarterPeriods[3].disabled,
      },
    ].filter(
      (period) => !period.ends || dayjs(period.ends).endOf('day').isAfter(now),
    );

    const yearPeriod = {
      label: `FY: ${compactRange(
        quarterPeriods[0].starts || '',
        quarterPeriods[3].ends || '',
      )}`,
      value: `year-${quarterPeriods[0].starts}`,
      starts: quarterPeriods[0].starts,
      ends: quarterPeriods[3].ends,
      disabled: quarterPeriods.some((period) => period.disabled),
    };

    const customPeriod = periods.find((period) => period.value === 'custom');

    return [
      ...availableQuarters,
      ...halfPeriods,
      yearPeriod,
      {
        label: 'Custom date range',
        value: 'custom',
        disabled: customPeriod?.disabled,
      },
    ];
  }, [periods]);

  const currentPeriodValue = useMemo(() => {
    const current = currentMonthRange();
    return goalPeriods.find(
      (period) =>
        period.starts === current.starts && period.ends === current.ends,
    )?.value;
  }, [currentMonthRange, goalPeriods]);

  return {goalPeriods, currentPeriodValue};
};
